import "./SongsheetCarousel.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBookOpen,
  faCog,
  faFileLines,
  faPeopleGroup,
  faShareAlt,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { CyCarousel } from "./CyCarousel";
import { RoutePaths } from "../RoutePaths";
import { faMusic } from "@fortawesome/free-solid-svg-icons/faMusic";
import { CarouselSidebar } from "./CarouselSidebar";
import { useTranslation } from "react-i18next";

interface Props {
  playlistIdentifier: string;
  openSidebar: CarouselSidebar | undefined;
  toggleSidebar: (sidebar: CarouselSidebar) => void;
  /** without permission to read songsheets, only the lyrics mode exists */
  canShowSongsheets: boolean;
  isBeamerModeActive: boolean;
  setIsBeamerModeActive: (active: boolean) => void;
}

interface ToolbarItemProps {
  icon: IconDefinition;
  label: string;
  active?: boolean;
  /** renders a link if set, a button otherwise */
  to?: string;
  onClick?: () => void;
  "data-cy"?: string;
}

/**
 * Styled like the nav items of the MainMenu. Label visibility and item size are set purely
 * by CSS, depending on the screen size (see .toolbar in SongsheetCarousel.css).
 */
function ToolbarItem({ icon, label, active, to, ...rest }: ToolbarItemProps) {
  const className = "nav-link " + (active ? "active" : "link-body-emphasis");
  const content = (
    <>
      <FontAwesomeIcon icon={icon} fixedWidth />
      <span className="toolbar-label">{label}</span>
    </>
  );

  return (
    <li className="nav-item">
      {to ? (
        <Link to={to} className={className} aria-label={label} {...rest}>
          {content}
        </Link>
      ) : (
        <button
          type="button"
          className={className}
          aria-label={label}
          aria-pressed={active}
          {...rest}
        >
          {content}
        </button>
      )}
    </li>
  );
}

interface ViewModeSwitchProps {
  isBeamerModeActive: boolean;
  setIsBeamerModeActive: (active: boolean) => void;
}

/**
 * Segmented control between songsheets and lyrics mode. Unlike the nav items it switches a state
 * instead of opening something, so it is framed differently and always shows both options.
 */
function ViewModeSwitch(props: ViewModeSwitchProps) {
  const { t } = useTranslation();
  const options = [
    {
      beamerMode: false,
      icon: faMusic,
      label: t("carousel.toolbar.songsheets"),
      cy: CyCarousel.SongsheetsModeButton,
    },
    {
      beamerMode: true,
      icon: faFileLines,
      label: t("carousel.toolbar.lyrics"),
      cy: CyCarousel.LyricsModeButton,
    },
  ];

  return (
    <li className="nav-item">
      <div
        className="toolbar-view-mode"
        role="radiogroup"
        aria-label={t("carousel.toolbar.viewMode")}
      >
        {options.map((option) => {
          const checked = props.isBeamerModeActive === option.beamerMode;
          return (
            <button
              key={option.label}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-label={option.label}
              className={"btn" + (checked ? " active" : "")}
              onClick={() => props.setIsBeamerModeActive(option.beamerMode)}
              data-cy={option.cy}
            >
              <FontAwesomeIcon icon={option.icon} fixedWidth />
              <span className="toolbar-label">{option.label}</span>
            </button>
          );
        })}
      </div>
    </li>
  );
}

export function CarouselToolbar(props: Props) {
  const { t } = useTranslation();

  return (
    <>
      <div className="toolbar-group">
        <ul className="nav nav-pills flex-column">
          <ToolbarItem
            to={RoutePaths.HOME}
            icon={faArrowLeft}
            label={t("carousel.toolbar.back")}
          />
          <ToolbarItem
            onClick={() => props.toggleSidebar("playlist")}
            active={props.openSidebar === "playlist"}
            data-cy={CyCarousel.PlaylistSidebarButton}
            icon={faBookOpen}
            label={t("carousel.toolbar.playlist")}
          />
        </ul>

        {props.canShowSongsheets && (
          <ul className="nav flex-column">
            <ViewModeSwitch
              isBeamerModeActive={props.isBeamerModeActive}
              setIsBeamerModeActive={props.setIsBeamerModeActive}
            />
          </ul>
        )}
      </div>

      <ul className="nav nav-pills flex-column">
        <ToolbarItem
          onClick={() => props.toggleSidebar("share")}
          active={props.openSidebar === "share"}
          data-cy={CyCarousel.ShareSidebarButton}
          icon={faShareAlt}
          label={t("carousel.toolbar.share")}
        />
        <ToolbarItem
          onClick={() => props.toggleSidebar("livePosition")}
          active={props.openSidebar === "livePosition"}
          data-cy={CyCarousel.PlaylistLivePositionSidebarButton}
          icon={faPeopleGroup}
          label={t("carousel.toolbar.sharedMode")}
        />
        <ToolbarItem
          onClick={() => props.toggleSidebar("settings")}
          active={props.openSidebar === "settings"}
          data-cy={CyCarousel.SettingsSidebarButton}
          icon={faCog}
          label={t("carousel.toolbar.settings")}
        />
      </ul>
    </>
  );
}
